import { useState, type FormEvent } from "react";
import type { Page } from "../types";

const interestOptions = [
    "Site vitrine",
    "Application web sur mesure",
    "Commande en ligne (restauration)",
    "Gestion de cantine scolaire",
    "Application progressive (PWA)",
    "Maintenance / évolution d'un projet existant",
    "Autre",
];

export function ContactForm({ onNavigate }: { onNavigate: (p: Page) => void }) {
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [interests, setInterests] = useState<string[]>([]);
    const [message, setMessage] = useState("");
    const [website, setWebsite] = useState(""); // honeypot
    const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const toggleInterest = (interest: string) => {
        setInterests((prev) =>
            prev.includes(interest) ? prev.filter((i) => i !== interest) : [...prev, interest]
        );
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ name, email, interests, message, website }),
            });

            const data = await res.json();

            if (!res.ok) {
                setStatus("error");
                setErrorMsg(data.error || "Une erreur est survenue.");
                return;
            }

            setStatus("success");
        } catch {
            setStatus("error");
            setErrorMsg("Impossible de contacter le serveur. Réessayez plus tard.");
        }
    };

    if (status === "success") {
        return (
            <section className="pt-32 pb-20 px-6 min-h-screen flex items-center justify-center">
                <div className="max-w-lg mx-auto text-center">
                    <div className="text-6xl mb-6">✉️</div>
                    <h2 className="text-3xl font-bold text-[#1a3660] mb-4">Message envoyé&nbsp;!</h2>
                    <p className="text-slate-500 mb-8">
                        Merci pour votre message. Nous vous répondrons dans les meilleurs délais.
                    </p>
                    <button
                        onClick={() => onNavigate("home")}
                        className="bg-[#f07020] hover:bg-[#d9621a] text-white font-semibold px-8 py-3 rounded-xl transition-colors cursor-pointer"
                    >
                        Retour à l'accueil
                    </button>
                </div>
            </section>
        );
    }

    return (
        <section className="pt-32 pb-20 px-6">
            <div className="max-w-2xl mx-auto">
                <button
                    onClick={() => onNavigate("home")}
                    className="inline-flex items-center gap-2 text-slate-500 hover:text-[#f07020] mb-8 transition-colors cursor-pointer"
                >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Retour
                </button>

                <h2 className="text-3xl sm:text-4xl font-bold text-[#1a3660] mb-2">
                    Contactez-<span className="text-[#f07020]">nous</span>
                </h2>
                <p className="text-slate-500 mb-10">
                    Décrivez votre projet en quelques clics. Nous vous répondrons rapidement.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Honeypot — hidden from users */}
                    <div className="absolute -left-[9999px]" aria-hidden="true">
                        <label htmlFor="website">Website</label>
                        <input
                            id="website"
                            type="text"
                            value={website}
                            onChange={(e) => setWebsite(e.target.value)}
                            tabIndex={-1}
                            autoComplete="off"
                        />
                    </div>

                    {/* Name */}
                    <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-[#1a3660] mb-2">
                            Votre nom *
                        </label>
                        <input
                            id="name"
                            type="text"
                            required
                            minLength={2}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#f07020] focus:ring-2 focus:ring-[#f07020]/20 outline-none transition-all text-slate-700"
                            placeholder="Jean Dupont"
                        />
                    </div>

                    {/* Email */}
                    <div>
                        <label htmlFor="email" className="block text-sm font-semibold text-[#1a3660] mb-2">
                            Votre email *
                        </label>
                        <input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#f07020] focus:ring-2 focus:ring-[#f07020]/20 outline-none transition-all text-slate-700"
                            placeholder="jean@exemple.fr"
                        />
                    </div>

                    {/* Interests */}
                    <fieldset>
                        <legend className="block text-sm font-semibold text-[#1a3660] mb-3">
                            Quel type de projet vous intéresse&nbsp;?
                        </legend>
                        <div className="grid sm:grid-cols-2 gap-3">
                            {interestOptions.map((opt) => (
                                <label
                                    key={opt}
                                    className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${interests.includes(opt)
                                        ? "border-[#f07020] bg-orange-50 text-[#1a3660]"
                                        : "border-slate-200 hover:border-slate-300 text-slate-600"
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={interests.includes(opt)}
                                        onChange={() => toggleInterest(opt)}
                                        className="accent-[#f07020] w-4 h-4"
                                    />
                                    <span className="text-sm">{opt}</span>
                                </label>
                            ))}
                        </div>
                    </fieldset>

                    {/* Message */}
                    <div>
                        <label htmlFor="message" className="block text-sm font-semibold text-[#1a3660] mb-2">
                            Décrivez votre projet
                        </label>
                        <textarea
                            id="message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            maxLength={5000}
                            rows={5}
                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#f07020] focus:ring-2 focus:ring-[#f07020]/20 outline-none transition-all resize-y text-slate-700"
                            placeholder="Parlez-nous de votre activité, de vos besoins, de vos idées..."
                        />
                    </div>

                    {/* Error */}
                    {status === "error" && (
                        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                            {errorMsg}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={status === "sending"}
                        className="w-full sm:w-auto bg-[#f07020] hover:bg-[#d9621a] disabled:bg-[#f07020]/50 text-white font-semibold px-8 py-3 rounded-xl transition-colors text-lg shadow-lg shadow-orange-200 cursor-pointer disabled:cursor-not-allowed"
                    >
                        {status === "sending" ? "Envoi en cours…" : "Envoyer"}
                    </button>
                </form>
            </div>
        </section>
    );
}
